import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Google as GoogleIcon, Handshake as HandshakeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import PartnerLanguageSelector from '../../components/partner/PartnerLanguageSelector';
import { partnerTheme, partnerHeroGradient } from '../../components/partner/partnerTheme';

// Login del PORTAL PARTNER — POST /partner/auth/login. Look cálido propio.

const PartnerLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, isAuthenticated, loading } = usePartnerAuth();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Spinner propio: el consentimiento de Google lo marca el usuario en el popup,
  // no el envío del formulario.
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate('/partner');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login({ email, password });
      navigate('/partner');
    } catch (err: any) {
      // PortalLoginError del contrato F1: mensaje ya traducido (credenciales +
      // intentos restantes, bloqueo con minutos, inactivo, partner suspendido).
      setError(
        err?.message ||
          err?.detail ||
          t('partner.auth.loginError', 'No se pudo iniciar sesión. Revisa tus credenciales.')
      );
    } finally {
      setIsLoading(false);
    }
  };

  // «Entrar con Google»: EXACTAMENTE el mismo SSO que el portal cliente y que
  // WAI (Composio vía el auth-service global). Ni una credencial propia.
  const handleGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      navigate('/partner');
    } catch (err: any) {
      setError(
        err?.message ||
          t('partner.auth.loginError', 'No se pudo iniciar sesión. Revisa tus credenciales.')
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <ThemeProvider theme={partnerTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: partnerHeroGradient
        }}
      >
        <Container maxWidth="sm">
          {/* El idioma se elige ANTES de entrar: en el login no hay navbar (que es
              donde vive el selector del resto del portal), así que sin esto un
              partner de habla inglesa se topaba con el formulario en español y sin
              forma de cambiarlo. Mismo criterio que la landing del programa. */}
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <PartnerLanguageSelector onHero />
          </Box>
          <Paper elevation={0} sx={{ p: 5, borderRadius: 4 }}>
            <Box textAlign="center" mb={4}>
              <HandshakeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                {t('partner.auth.title', 'Portal Partner')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('partner.auth.subtitle', 'Gestiona tu cartera de clientes y tus créditos mayoristas')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('partner.auth.email', 'Email')}
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={t('partner.auth.password', 'Contraseña')}
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                required
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                {isLoading
                  ? t('partner.auth.loggingIn', 'Entrando...')
                  : t('partner.auth.login', 'Entrar')}
              </Button>
            </form>

            <Divider sx={{ mt: 3, mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                {t('portalAuth.or', 'o')}
              </Typography>
            </Divider>
            <Button
              onClick={handleGoogleLogin}
              fullWidth
              variant="outlined"
              size="large"
              disabled={googleLoading || isLoading}
              startIcon={googleLoading ? <CircularProgress size={18} /> : <GoogleIcon />}
              sx={{ mt: 1, py: 1.25, fontWeight: 600, textTransform: 'none' }}
              data-testid="partner-login-google-btn"
            >
              {googleLoading
                ? t('portalAuth.googleConnecting', 'Conectando con Google...')
                : t('portalAuth.googleSignIn', 'Entrar con Google')}
            </Button>

            {/* Salida para quien AÚN no es partner: la landing comercial del
                programa (pública) en vez de un «escríbenos» sin destino. */}
            <Box textAlign="center" mt={3}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {t('partner.auth.hint', '¿Aún no eres partner de WAI? Así funciona el programa y así te damos de alta.')}
              </Typography>
              <Button
                variant="text"
                startIcon={<HandshakeIcon />}
                onClick={() => navigate('/partner/programa')}
                sx={{ fontWeight: 700 }}
              >
                {t('partner.auth.programCta', '¿Quieres ser partner de WAI?')}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default PartnerLoginPage;
